/**************************************************************************************************************************
 Copyright (c) 2024 WorkEarly Results LLP

     Name: basic.components.tsx
   Author: Harikrishnan Gangadharan
 Comments: 

/**************************************************************************************************************************
 IMPORTS
***************************************************************************************************************************/
import { ImageAsset } from '@/constants';
import { motion } from 'framer-motion';
import { FunctionComponent } from 'react';
/**************************************************************************************************************************
 TYPES / GLOBAL DEFINITIONS
***************************************************************************************************************************/
interface SectionHeaderProps {
  title: string;
  subtitle: string;
}

interface PageIntroProps {
  asset: ImageAsset;
  title: string;
  desc: String;
}
/**************************************************************************************************************************
 IMPLEMENTATIONS
***************************************************************************************************************************/
const SectionHeader: FunctionComponent<SectionHeaderProps> = ({
  title,
  subtitle
}: SectionHeaderProps) => {
  return (
    <motion.div
      className="relative w-fit h-fit flex items-center justify-start gap-3 pr-5"
      initial="initial"
      whileInView="entry"
      viewport={{ once: true }}
      variants={{
        initial: {
          x: -50,
          opacity: 0
        },
        entry: {
          x: 0,
          opacity: 1
        }
      }}
      transition={{
        duration: 0.75,
        delay: 0.5
      }}
    >
      <div className="h-full flex flex-col items-start justify-center gap-1 py-2">
        <h4 className="text-md md:text-lg lg:text-xl text-white font-roboto font-medium uppercase">
          {subtitle}
        </h4>
        <div className="bg-theme-900 w-full h-[2px]"></div>
        <h3 className="text-2xl md:text-3xl lg:text-4xl text-theme-900 font-montserrat font-bold uppercase">
          {title}
        </h3>
      </div>
    </motion.div>
  );
};

const PageIntro: FunctionComponent<PageIntroProps> = ({ asset, title, desc }: PageIntroProps) => {
  return (
    <section className="relative bg-theme-900 w-full min-h-[22vh] shadow-md drop-shadow-md">
      <div className="absolute top-0 left-0 w-full h-full flex flex-col items-center justify-end gap-4 px-5 pb-10">
        <motion.h1
          className="max-w-screen-xl mx-auto w-full text-4xl lg:text-5xl text-left text-black font-poppins font-bold uppercase"
          initial="initial"
          whileInView="entry"
          viewport={{ once: true }}
          variants={{
            initial: {
              x: 100,
              opacity: 0
            },
            entry: {
              x: 0,
              opacity: 1
            }
          }}
          transition={{
            duration: 0.5
          }}
        >
          {title}
        </motion.h1>
        <motion.p
          className="max-w-screen-xl mx-auto w-full text-md md:text-lg lg:text-xl text-orange-900 text-left font-inter font-medium"
          initial="initial"
          whileInView="entry"
          viewport={{ once: true }}
          variants={{
            initial: {
              x: -100,
              opacity: 0
            },
            entry: {
              x: 0,
              opacity: 1
            }
          }}
          transition={{
            duration: 0.75,
            delay: 0.5
          }}
        >
          {desc}
        </motion.p>
      </div>
    </section>
  );
};

const BasicComponents = {
  SectionHeader,
  PageIntro
};

export default BasicComponents;
